import { useGetCurrentUserQuery } from "@/shared/services";
import { useIsOnboardingCohort } from "@features/onboarding/cohort";
import { ONBOARDING_EVENTS, track } from "@features/analytics";
import { IconListCheck, IconX } from "@tabler/icons-react";
import { useSidebarStore } from "@/components/sidebar/store";
import { usePlatformChecklistStoreShallow } from "../checklist-store";
import { useNavigate } from "react-router-dom";
import { hubEntry, ONBOARDING_HUB_PATH } from "../constants";
import { usePlatformUiStoreShallow } from "../ui-store";
import { useVariantOnboarding } from "../use-onboarding-variant";

export const OnboardingProgressCard = () => {
  const navigate = useNavigate();
  const isSidebarOpen = useSidebarStore((s) => s.isOpen);
  const { data: me } = useGetCurrentUserQuery();
  const completed = usePlatformChecklistStoreShallow((s) => s.completed);
  const { sidebarDismissed, patch } = usePlatformUiStoreShallow((s) => ({
    sidebarDismissed: s.sidebarDismissed,
    patch: s.patch,
  }));
  const { isReady, tasks, total, taskSet } = useVariantOnboarding();
  const isCohort = useIsOnboardingCohort(me?.createdAt);

  const visible = new Set(tasks.map((task) => task.key));
  const doneCount = completed.filter((key) => visible.has(key)).length;

  if (!isReady) return null;
  if (!isSidebarOpen) return null;
  if (sidebarDismissed) return null;
  if (!isCohort) return null;
  if (doneCount >= total) return null;

  const progress = Math.round((doneCount / total) * 100);

  return (
    <div className="tw-relative tw-px-4 tw-py-2">
      <button
        type="button"
        onClick={() => navigate(ONBOARDING_HUB_PATH, hubEntry("sidebar card"))}
        className="tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-gap-3 tw-rounded-xl tw-border-0 tw-bg-white tw-px-4 tw-py-2 tw-text-left tw-shadow-2xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 tw-transition-[background-color,box-shadow] hover:tw-bg-[#FBFBFC] hover:tw-shadow-xs"
      >
        <div className="tw-flex tw-h-6 tw-items-center tw-gap-2 tw-pr-8">
          <IconListCheck
            size={16}
            stroke={1.5}
            className="tw-fill-none tw-text-blue"
          />
          <span className="tw-font-sans tw-text-sm tw-font-medium tw-leading-5 tw-text-blue">
            Onboarding
          </span>
        </div>
        <div
          role="progressbar"
          aria-label="Onboarding progress"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-blue-50"
        >
          <div
            className="tw-h-full tw-rounded-full tw-bg-primary-base"
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="tw-font-sans tw-text-xs tw-font-normal tw-leading-4 tw-text-blue-500">
          {progress}% completed
        </span>
      </button>
      <button
        type="button"
        aria-label="Hide onboarding progress"
        onClick={() => {
          track(ONBOARDING_EVENTS.DISMISS_ONBOARDING_PROGRESS_CARD, {
            "completed count": doneCount,
            "total tasks": total,
            "task set": taskSet,
          });
          patch({ sidebarDismissed: true });
        }}
        className="tw-bg-transparent tw-absolute tw-right-8 tw-top-4 tw-flex tw-size-6 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-p-0 tw-text-blue-500 hover:tw-bg-blue-100/50 hover:tw-text-blue"
      >
        <IconX size={16} stroke={1.5} className="tw-fill-none" />
      </button>
    </div>
  );
};
